import { useMemo } from "react";
import { ConnectionHandler, graphql } from "react-relay";
import { useSubscriptionWithSkip } from "../utils/useSubscriptionWithSkip";
import { useWidgetTodoSubscriptionChangedSubscription } from "./__generated__/useWidgetTodoSubscriptionChangedSubscription.graphql";
import { useWidgetTodoSubscriptionCreatedSubscription } from "./__generated__/useWidgetTodoSubscriptionCreatedSubscription.graphql";
import { useWidgetTodoSubscriptionDeletedSubscription } from "./__generated__/useWidgetTodoSubscriptionDeletedSubscription.graphql";
import { useWidgetTodoSubscriptionTodoAssignedSubscription } from "./__generated__/useWidgetTodoSubscriptionTodoAssignedSubscription.graphql";
import { useWidgetTodoSubscriptionUnassignedSubscription } from "./__generated__/useWidgetTodoSubscriptionUnassignedSubscription.graphql";

export function useWidgetTodoSubscription(
  teamIds: string[],
  todoIds: string[],
  categoryId: string | undefined,
  connection: string | undefined,
  interactive: boolean | undefined
) {
  useSubscriptionWithSkip<useWidgetTodoSubscriptionChangedSubscription>(
    useMemo(
      () => ({
        variables: { todoIds },
        subscription: graphql`
          subscription useWidgetTodoSubscriptionChangedSubscription(
            $todoIds: [ID!]!
          ) {
            todoChangedByIds(todoIds: $todoIds) {
              id
              ordinal
              ...TodoOverviewListItemWidget_Todo
            }
          }
        `,
      }),
      [todoIds]
    ),
    {
      skip: !categoryId || interactive === false,
    }
  );

  useSubscriptionWithSkip<useWidgetTodoSubscriptionTodoAssignedSubscription>(
    useMemo(
      () => ({
        variables: {
          teamIds,
          categoryId: categoryId!,
          connections: connection ? [connection] : [],
        },
        subscription: graphql`
          subscription useWidgetTodoSubscriptionTodoAssignedSubscription(
            $categoryId: ID!
            $teamIds: [ID!]!
            $connections: [ID!]!
          ) {
            todoAssignedToTeams(categoryIds: [$categoryId], teamIds: $teamIds) {
              todo
                @prependNode(
                  connections: $connections
                  edgeTypeName: "TodoEdge"
                ) {
                id
                ordinal
                status
                ...TodoOverviewListItemWidget_Todo
              }
            }
          }
        `,
      }),
      [categoryId, connection, teamIds]
    ),
    {
      skip: !categoryId || interactive === false,
    }
  );
  useSubscriptionWithSkip<useWidgetTodoSubscriptionUnassignedSubscription>(
    useMemo(
      () => ({
        variables: { teamIds, categoryId: categoryId! },
        subscription: graphql`
          subscription useWidgetTodoSubscriptionUnassignedSubscription(
            $categoryId: ID!
            $teamIds: [ID!]!
          ) {
            todoUnassignedToTeams(
              categoryIds: [$categoryId]
              teamIds: $teamIds
            ) {
              todo {
                id
              }
            }
          }
        `,
        updater: (store, result) => {
          if (connection && result.todoUnassignedToTeams?.todo.id) {
            const conn = store.get(connection);
            if (conn) {
              ConnectionHandler.deleteNode(
                conn,
                result.todoUnassignedToTeams?.todo.id
              );
            }
          }
        },
      }),
      [categoryId, connection, teamIds]
    ),
    {
      skip: !categoryId || interactive === false,
    }
  );

  // TODO: add to connection
  // ,

  useSubscriptionWithSkip<useWidgetTodoSubscriptionCreatedSubscription>(
    useMemo(
      () => ({
        variables: {
          teamIds,
          categoryId: categoryId!,
          connections: connection ? [connection] : [],
        },

        subscription: graphql`
          subscription useWidgetTodoSubscriptionCreatedSubscription(
            $categoryId: ID!
            $teamIds: [ID!]!
            $connections: [ID!]!
          ) {
            todoCreatedNew(categoryId: $categoryId, teamIds: $teamIds) {
              todo
                @prependNode(
                  connections: $connections
                  edgeTypeName: "TodoEdge"
                ) {
                id
                ordinal
                status
                ...TodoOverviewListItemWidget_Todo
              }
            }
          }
        `,
      }),
      [categoryId, connection, teamIds]
    ),
    {
      skip: !categoryId || interactive === false,
    }
  );

  useSubscriptionWithSkip<useWidgetTodoSubscriptionDeletedSubscription>(
    useMemo(
      () => ({
        variables: { teamIds, categoryId: categoryId! },
        subscription: graphql`
          subscription useWidgetTodoSubscriptionDeletedSubscription(
            $categoryId: ID!
            $teamIds: [ID!]!
          ) {
            todoDeleted(categoryIds: [$categoryId], teamIds: $teamIds) {
              id @deleteRecord
            }
          }
        `,
      }),
      [categoryId, teamIds]
    ),
    {
      skip: !categoryId || interactive === false,
    }
  );
}
